/*
 *
 * VerifyOtp
 *
 * @version 1.0.0
 * @author a.mancuso
 * @since 23-Apr-2025
 */

import React, { useEffect, useImperativeHandle, forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import formattedMessage from './languageModule'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'
import PortalOtp from '@nexidigital/portalotp'
import PortalButton from '@nexidigital/portalbutton'
// import { useMobileDetect } from 'Hoc/ListenerProvider'
import MerchantFlowCard from '@nexidigital/merchantflowcard'
import FlowButtons from '@nexidigital/flowbuttons'
import { addSpaceBeforeAsterisks } from 'Utility/utils'
import './VerifyOtp.scss'

const VerifyOtp = forwardRef((props, ref) => {
  const intl = useIntl()
  const [otpKey, setOtpKey] = useState(0)

  const OTP_KEY = 'phoneOtp'

  const initialValues = {
    [OTP_KEY]: ''
  }

  const validationSchema = Yup.object().shape({
    [OTP_KEY]: Yup.string()
      .required(formattedMessage.requiredError)
      .min(
        6,
        intl.formatMessage({
          id: 't.app.registration.error.invalidPhoneOtpFormat'
        })
      )
  })

  const handleChange = ({ otpString }) => {
    props.actions.doHideToast()
    formik.setFieldValue(OTP_KEY, otpString)
  }

  const handleSubmit = (values, { setSubmitting }) => {
    props.actions.doVerifyOtp({
      otp: values[OTP_KEY]
    })
    setSubmitting(true)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  const handleGenerateNewPhoneOTP = () => {
    formik.setFieldValue(OTP_KEY, '')
    formik.setFieldTouched(OTP_KEY, false)
    formik.setErrors({})
    formik.resetForm()
    setOtpKey(prev => prev + 1)
    props.actions.doRegenerateOtp({ newOtp: true })
  }

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formik.handleSubmit()
    }
  }), [formik])

  useEffect(() => {
    if (props.otpError) {
      formik.setErrors({ [OTP_KEY]: intl.formatMessage({ id: 't.app.error.toast.wrongOtp' }) })
    } else if (props.errorData.code) {
      formik.setErrors({ [OTP_KEY]: props.errorData.message })
    }
  }, [props.errorData, props.otpError])

  useEffect(
    () => () => {
      // Only reset if not disabled (for SCA flows that need to preserve nextAction)
      if (!props.disableAutoReset) {
        props.actions.doResetState()
      }
    },
    [props.disableAutoReset]
  )

  return (
    <div className='verifyOtpContainer'>
      <MerchantFlowCard
        title={props.titleCard ? props.titleCard : formattedMessage.cardTitle}
        subtitle={props.subtitleCard ? props.subtitleCard : formattedMessage.cardSubTitle(addSpaceBeforeAsterisks(props.maskedPhoneFromPersistentData ? props.maskedPhoneFromPersistentData : props.maskedPhoneNumber))}
      >
        <div className='confirmNumber'>
          <PortalOtp
            key={otpKey}
            id='portalOtpInput'
            name='portalOtpInput'
            disabled={props.isLoading}
            error={Boolean(formik.touched[OTP_KEY] && formik.errors[OTP_KEY])}
            customTextError={formik.touched[OTP_KEY] && formik.errors[OTP_KEY]}
            onFocus={() => formik.setFieldTouched(OTP_KEY, true, false)}
            onBlur={formik.handleBlur}
            onChange={handleChange}
            noIcon
          />
          <PortalButton
            textButton
            size='small'
            id='sendCodeLink'
            label={formattedMessage.labelLink}
            onClick={handleGenerateNewPhoneOTP}
            disabled={props.isLoading}
          />
        </div>
      </MerchantFlowCard>
      {!props.hidePrimaryButton && <FlowButtons
        idPrimary='verifyOtpPrimaryButton'
        labelPrimary={props.labelPrimaryButton}
        onClickPrimary={() => formik.handleSubmit()}
        isPrimaryLoading={props.isLoading}
                                   />}
    </div>
  )
})

VerifyOtp.propTypes = {
  actions: PropTypes.object,
  isLoading: PropTypes.bool,
  errorData: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string
  }),
  maskedPhoneNumber: PropTypes.string,
  labelPrimaryButton: PropTypes.string,
  titleCard: PropTypes.string,
  subtitleCard: PropTypes.string,
  hidePrimaryButton: PropTypes.bool,
  disableAutoReset: PropTypes.bool,
  otpError: PropTypes.string,
  maskedPhoneFromPersistentData: PropTypes.string
}

VerifyOtp.defaultProps = {
  actions: {
    doHideToast: () => { },
    doRegenerateOtp: () => { },
    doVerifyOtp: () => { },
    doResetState: () => { }
  },
  isLoading: false,
  cardTitle: '',
  errorData: {
    code: '',
    message: ''
  },
  maskedPhoneNumber: '',
  titleCard: '',
  subtitleCard: '',
  labelPrimaryButton: formattedMessage.continue,
  hidePrimaryButton: false,
  disableAutoReset: false,
  otpError: '',
  maskedPhoneFromPersistentData: ''
}

VerifyOtp.displayName = 'VerifyOtp'

export default VerifyOtp
